// NavigationGate implementation moved from app/_layout.tsx
import * as Linking from 'expo-linking';
import { usePathname, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, AppState, Platform, TouchableOpacity, View } from 'react-native';

import { SignOutForm } from '@/components/SignInForm';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import AppStyles from '@/constants/Styles';
import { useAuth } from '@/hooks/useAutoLogin';
import { useBackgroundBranches, useBackgroundCampaigns } from '@/hooks/useBackgroundFetch';
import { useNavigationStore, type Branch, type Campaign } from '@/stores/navigationStore';
import { redeemAccessToken } from '@/utils/tokenRedemption';

// Re-export types from the store for backward compatibility
export type { Branch, Campaign, Election } from '@/stores/navigationStore';

export function useInitialUrl() {
  const [initialUrl, setInitialUrl] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const getUrl = async () => {
      const url = await Linking.getInitialURL();
      setInitialUrl(url);
      setChecked(true);
    };
    getUrl();
  }, []);

  return { initialUrl, checked };
}

// initial url is only process once per app session
let initialUrlProcessedAndDeepLinkingSetUp = false;

export function NavigationGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();  
  const { initialUrl, checked } = useInitialUrl();
  
  // Get token redemption state from authentication context
  const { setTokenRedemptionInProgress } = useAuth();
  
  // Use background fetch hooks for data
  const { data: branches, loading: branchesLoading, error: branchesError } = useBackgroundBranches();
  const { data: campaigns } = useBackgroundCampaigns(
    useNavigationStore.getState().getSelectedBranch()?.branch_short_name || ''
  );
  
  // Use Zustand store for actions and state
  const {
    getSelectedBranch,
    getSelectedCampaign,
    changeBranch,
    setSelectedCampaignId,
    getBranchData,
  } = useNavigationStore();
  
  // Get current branch and campaign from store
  const selectedBranch = getSelectedBranch();
  const selectedCampaign = getSelectedCampaign();
  
  // Local state for navigation flow control
  const [branchSelectionFinished, setBranchSelectionFinished] = React.useState(false);
  const [campaignSelectionFinished, setCampaignSelectionFinished] = React.useState(false);
  
  // Track which specific token has been processed to avoid re-processing the same token
  // but allow processing different tokens
  const [processedToken, setProcessedToken] = React.useState<string | null>(null);

  // Initialize tokenRedemptionInProgress by checking for token in URL synchronously
  // This prevents children from rendering (and making API calls) before token redemption completes
  // This is only an issue on web
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const currentToken = urlParams.get('token');
      
      // Only set redemption in progress if there's a token and it's different from the last processed one
      if (currentToken && currentToken !== processedToken) {
        setTokenRedemptionInProgress(true);
      }
    }
  }, [setTokenRedemptionInProgress, processedToken]);


  const selectBranch = async (branchesList: Branch[]) => {
    let branchParam = null;
    const pathParts = pathname.split('/').filter(Boolean);
    if (pathParts[0]) branchParam = pathParts[0];
    
    if (branchParam) {
      const found = branchesList.find((b: Branch) => b.branch_short_name === branchParam);
      if (found) {
        await changeBranch(found);
      }
    } else {
      // Check if we have a stored branch in the store
      if (selectedBranch) {
        // Branch already selected from store
      } else if (branchesList.length === 1 && branchesList[0]) {
        await changeBranch(branchesList[0]);
      }
    }
  };

  // Select campaign based on current campaigns and URL/path
  const selectCampaign = async (branch: Branch, campaignsList: Campaign[]) => {
    const pathParts = pathname.split('/').filter(Boolean);
    let campaignParam = null;
    if (pathParts[1] === 'campaign' && pathParts[2]) campaignParam = pathParts[2];
    
    if (campaignParam) {
      const found = campaignsList.find((c: Campaign) => c.boundary_id === campaignParam);
      if (found) {
        setSelectedCampaignId(branch.branch_short_name, found.boundary_id);
        return found;
      }
    } else {
      // Check if we have a stored campaign in the store
      const branchData = getBranchData(branch.branch_short_name);
      if (branchData?.selectedCampaignId) {
        const storedCampaign = campaignsList.find(c => c.boundary_id === branchData.selectedCampaignId);
        if (storedCampaign) {
          if (!initialUrl) {
            router.replace(`/${branch.branch_short_name}/campaign/${storedCampaign.boundary_id}`);
          }
          return storedCampaign;
        }
      } else if (campaignsList.length === 1 && campaignsList[0]) {
        setSelectedCampaignId(branch.branch_short_name, campaignsList[0].boundary_id);
        return campaignsList[0];
      }
    }
    
    setSelectedCampaignId(branch.branch_short_name, null);
    return null;
  };

  const navigateIfNecessary = async (selectedBranchParam: Branch | null, selectedCampaignParam: Campaign | null) => {
    const isBasePath = pathname === '/' || pathname === '';
    if (isBasePath && !initialUrl) {
      if (!selectedBranchParam) {
        return;
      }
      if (selectedCampaignParam) {
        router.replace(`/${selectedBranchParam.branch_short_name}/campaign/${selectedCampaignParam.boundary_id}`);
      } else {
        router.replace(`/${selectedBranchParam.branch_short_name}/campaign/`);
      }
    }
  };


  // Unified URL handler for both cold start and warm start deep links
  const handleDeepLink = async (url: string | null) => {
    if (!url) return;
    
    try {
      const parsedUrl = new URL(url);
      const token = parsedUrl.searchParams.get('token');

      if (!token) return;
      
      // Skip if we've already processed this exact token
      if (token === processedToken) {
        console.log('[NavigationGate] Token already processed, skipping');
        return;
      }
      
      setTokenRedemptionInProgress(true);
      await redeemAccessToken(token);
      
      // Mark this specific token as processed to prevent re-processing
      setProcessedToken(token);
      
      // Clean up the URL on web to remove the token parameter
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('token');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
      
      // Navigate to the path from the URL
      const targetPath = parsedUrl.pathname;
      if (targetPath && targetPath !== '/') {
        console.log('[NavigationGate] Navigating to:', targetPath);
        router.push(targetPath as any);
      }
      
      setTokenRedemptionInProgress(false);

    } catch (error) {
      console.error('[NavigationGate] Error processing deep link:', error);
      // Mark token as processed even on error to prevent infinite loop
      try {
        const parsedUrl = new URL(url);
        const token = parsedUrl.searchParams.get('token');
        if (token) setProcessedToken(token);
      } catch (parseError) {
        // If we can't parse the URL, just log and continue
        console.error('[NavigationGate] Failed to parse URL in error handler:', parseError);
      }
      setTokenRedemptionInProgress(false);
    }
  };

  // Unified deep link handling - runs once on mount
  useEffect(() => {    
    let linkingListener: any = null;
    
    const setupDeepLinkHandling = async () => {
      // Handle initial URL (cold start)
      const initialUrlValue = await Linking.getInitialURL();
      if (initialUrlValue) {
        handleDeepLink(initialUrlValue);
      }

      // Handle deep links when app is already running (warm start)
      linkingListener = Linking.addEventListener('url', (event) => {
        console.log('[NavigationGate] Deep link received while app open');
        handleDeepLink(event.url);
      });

      // Handle web URL with token on initial load
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (token && window.location.href !== initialUrlValue) {
          handleDeepLink(window.location.href);
        }
      }
    };

    if (!initialUrlProcessedAndDeepLinkingSetUp) {
      initialUrlProcessedAndDeepLinkingSetUp = true;
      setupDeepLinkHandling();
    }

    // Cleanup function to remove event listener
    return () => {
      if (linkingListener) {
        linkingListener.remove();
      }
    };
  }, []); // Empty dependency array - only run once on mount
  
  // Refetch branches when app comes to foreground
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'active') {
        useNavigationStore.getState().clearExpiredBranches();
      }
    });

    return () => {
      subscription.remove();
    };
  }, []);
  
  useEffect(() => {
    const initialLoad = async () => {
      if (branches) {
        await selectBranch(branches);
        setBranchSelectionFinished(true);
      }
    }
    initialLoad();
  }, [branches, branchesLoading]);

  // Fetch elections when a branch is selected
  useEffect(() => {
    const fetchElectionsForBranch = async () => {
      if (selectedBranch) {
        try {
          const { fetchElections } = useNavigationStore.getState();
          await fetchElections(selectedBranch.branch_short_name);
        } catch (error) {
          console.warn('Failed to fetch elections on app load:', error);
        }
      }
    };
    
    fetchElectionsForBranch();
  }, [selectedBranch]);

  useEffect(() => {
    if (!selectedBranch || !campaigns) return;

    if (!checked) return;

    const loadData = async () => {
      await selectCampaign(selectedBranch, campaigns);
      setCampaignSelectionFinished(true);
    }

    loadData();    
  }, [selectedBranch, campaigns, checked]);

  useEffect(() => {
    if (!selectedBranch && !branchSelectionFinished) {
      return;
    }
    if (selectedBranch && !campaignSelectionFinished) {
      return;
    }
    if (!checked) {
      return;
    }
    
    (async () => {
      await navigateIfNecessary(selectedBranch, selectedCampaign);
    })();
  }, [branchSelectionFinished, selectedBranch, campaignSelectionFinished, checked]);

  // Show loading state while branches are loading
  if (branchesLoading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
        <ThemedText style={{ marginTop: 16, fontSize: 16, textAlign: 'center' }}>
          Loading...
        </ThemedText>
      </ThemedView>
    );
  }
  
  // Show error state if branches failed to load
  if (branchesError) {
    console.error('[NavigationGate] branches error:', branchesError);
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ThemedText style={{ color: 'red' }}>Error loading branches: {branchesError.message}</ThemedText>
      </ThemedView>
    );
  }

  const isScanPage = pathname === '/scan' || pathname === '/(main)/scan';
  if (branchSelectionFinished && branches && branches.length === 0 && !isScanPage) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 16, textAlign: 'center' }}>
          Welcome
        </ThemedText>
        <ThemedText style={{ fontSize: 16, textAlign: 'center', lineHeight: 24 }}>
          Welcome to the GroundForce app.
        </ThemedText>
        <ThemedText style={{ fontSize: 16, textAlign: 'center', lineHeight: 24 }}>
          Please {Platform.OS !== 'web' && "scan a QR code or "}click a link granting access to continue.
        </ThemedText>
        {Platform.OS !== 'web' && <ScanLinkButton />}
        <View style={{ marginTop: 32 }}>
          <SignOutForm />
        </View>
      </ThemedView>
    );
  }

  return <>{children}</>;
}

function ScanLinkButton() {
  const router = useRouter();
  
  return (
    <TouchableOpacity
      onPress={() => router.push('/scan')}
      style={[AppStyles.primaryButton, { marginTop: 32 }]}
    >
      <ThemedText style={AppStyles.primaryButtonText}>Scan QR Code for Access</ThemedText>
    </TouchableOpacity>
  );
} 